import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import * as actions from "../../store/actions/index";
import { withRouter } from "../../shared/utility";
import { useNavigate } from "react-router-dom";

import SimpleNotification from "../../components/SimpleNotification";
import EmptyModal from "../../components/modals/EmptyModal";
import SelectMenu from "../../components/controls/SelectMenu";
import Input from "../../components/controls/Input";
import MediaUi from "../../components/importMedia/MediaUi";
import ButtonSpinner from "../../components/controls/ButtonSpinner";
import DatePickerInput from "../../components/controls/DatePickerInput";

import CreateLabSampleViewModel from "../../viewmodels/labSamples/CreateLabSampleViewModel";

function CreateLabSample(props) {
  const { logout } = props;
  const navigate = useNavigate();

  const {
    labs,
    getLabsForDropdown,
    inProgress,
    createLabSample,
    setShowSimpleNotification,
    showSimpleNotification,
    emptyModal,
    setEmptyModelOpen,
  } = CreateLabSampleViewModel(logout);

  useEffect(() => {
    getLabsForDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [values, setValues] = useState({
    lab_id: "",
    lot_number: "",
    product_name: "",
    plu: "",
    manufactured_product_id: "",
    barcode: "",
    weight_in_kg: "",
    weight_in_lbs: "",
    piece_count: "",
    notes: "",
    lab_date: null,
    completed_date: null,
  });
  const [media1, setMedia1] = useState(null);
  const [media2, setMedia2] = useState(null);
  const [media3, setMedia3] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target || {};
    setValues({ ...values, [name]: value });
  };

  // react-select -> SelectMenu passes an event-like object via convertToDefEventPara
  // handleSelectEvent expects that shape and pulls the option value
  const handleSelectEvent = (e) => {
    const selected = e?.target?.value;
    // selected may be an object { value,label } or a primitive
    const lab_id =
      selected && typeof selected === "object" ? selected.value : selected;
    setValues({ ...values, lab_id });
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault && e.preventDefault();

    // clear previous field errors
    setFieldErrors({});

    // Basic client-side validation: ensure all fields are filled.
    // Assumption: all keys in `values` are required (lab selection must not be -1).
    const requiredKeys = Object.keys(values);
    const missing = requiredKeys.filter((k) => {
      const v = values[k];
      if (k === "lab_id") return !v || v === -1;
      return v === "" || v === null || v === undefined;
    });

    if (missing.length > 0) {
      // Build fieldErrors for inline display
      const errors = {};
      missing.forEach((k) => {
        errors[k] = ["This field is required"];
      });
      setFieldErrors(errors);

      // Friendly list of missing field names for the notification
      const pretty = missing.join(", ");
      setShowSimpleNotification({
        show: true,
        title: `Please fill required fields: ${pretty}`,
        type: 2,
      });
      return;
    }

    // light numeric validation
    if (values.weight_in_kg && isNaN(Number(values.weight_in_kg))) {
      setFieldErrors({ weight_in_kg: ["Amount in kg must be a number"] });
      setShowSimpleNotification({
        show: true,
        title: "Amount in kg must be a number",
        type: 2,
      });
      return;
    }

    if (values.weight_in_lbs && isNaN(Number(values.weight_in_lbs))) {
      setFieldErrors({ weight_in_lbs: ["Amount in lbs must be a number"] });
      setShowSimpleNotification({
        show: true,
        title: "Amount in lbs must be a number",
        type: 2,
      });
      return;
    }

    if (values.piece_count && !Number.isInteger(Number(values.piece_count))) {
      setFieldErrors({ piece_count: ["Piece count must be an integer"] });
      setShowSimpleNotification({
        show: true,
        title: "Piece count must be an integer",
        type: 2,
      });
      return;
    }

    const payload = { ...values };

    const files = { media1, media2, media3 };

    const result = await createLabSample(payload, files);
    if (result.success) {
      setShowSimpleNotification({
        show: true,
        title: "Sample created",
        type: 1,
      });
      navigate("/lab-samples");
      return;
    }

    // If server-side validation errors were returned, display them inline
    if (result.errors) {
      setFieldErrors(result.errors || {});

      // show a banner with the first field error message
      const firstKey = Object.keys(result.errors)[0];
      const firstMsg = result.errors[firstKey] && result.errors[firstKey][0];
      setShowSimpleNotification({
        show: true,
        title: firstMsg || result.message || "Please correct the errors",
        type: 2,
      });
      return;
    }

    // Generic failure - show message if provided
    if (result.message) {
      setShowSimpleNotification({
        show: true,
        title: result.message,
        type: 2,
      });
    }
  };

  return (
    <div>
      <SimpleNotification
        setShow={setShowSimpleNotification}
        showSimpleNotification={showSimpleNotification}
      />

      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />

      <div className="mt-2 px-4 sm:px-8">
        <div className="sm:flex sm:items-center px-4 py-5 sm:px-4 card-custom">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-gray-900">
              Create Lab Sample
            </h1>
          </div>
        </div>

        <form className="mt-6 card-custom p-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4">
            <SelectMenu
              handleInputChange={handleSelectEvent}
              name="laboratory"
              options={labs}
              value={labs.find((o) => o.value === values.lab_id) || null}
              error={fieldErrors.lab_id ? fieldErrors.lab_id[0] : null}
              title={"Laboratory"}
              showLabel={true}
            />

            <Input
              label="Lot number"
              name="lot_number"
              onChange={handleInputChange}
              value={values.lot_number}
              error={fieldErrors.lot_number ? fieldErrors.lot_number[0] : null}
              labelOn={true}
              type="text"
            />

            <Input
              label="Product name"
              name="product_name"
              onChange={handleInputChange}
              value={values.product_name}
              error={
                fieldErrors.product_name ? fieldErrors.product_name[0] : null
              }
              labelOn={true}
              type="text"
            />

            <Input
              label="PLU"
              name="plu"
              onChange={handleInputChange}
              value={values.plu}
              error={fieldErrors.plu ? fieldErrors.plu[0] : null}
              labelOn={true}
              type="text"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <Input
              label="Manufactured Product ID"
              name="manufactured_product_id"
              onChange={handleInputChange}
              value={values.manufactured_product_id}
              error={
                fieldErrors.manufactured_product_id
                  ? fieldErrors.manufactured_product_id[0]
                  : null
              }
              labelOn={true}
              type="text"
            />

            <Input
              label="Barcode"
              name="barcode"
              onChange={handleInputChange}
              value={values.barcode}
              error={fieldErrors.barcode ? fieldErrors.barcode[0] : null}
              labelOn={true}
              type="text"
            />

            <Input
              label="Amount in kg"
              name="weight_in_kg"
              onChange={handleInputChange}
              value={values.weight_in_kg}
              error={
                fieldErrors.weight_in_kg ? fieldErrors.weight_in_kg[0] : null
              }
              labelOn={true}
              type="number"
            />

            <Input
              label="Amount in lbs"
              name="weight_in_lbs"
              onChange={handleInputChange}
              value={values.weight_in_lbs}
              error={
                fieldErrors.weight_in_lbs ? fieldErrors.weight_in_lbs[0] : null
              }
              labelOn={true}
              type="number"
            />

            <Input
              label="Piece count"
              name="piece_count"
              onChange={handleInputChange}
              value={values.piece_count}
              error={
                fieldErrors.piece_count ? fieldErrors.piece_count[0] : null
              }
              labelOn={true}
              type="number"
            />

            <Input
              label="Notes"
              name="notes"
              onChange={handleInputChange}
              value={values.notes}
              error={fieldErrors.notes ? fieldErrors.notes[0] : null}
              labelOn={true}
              type="text"
            />

            <DatePickerInput
              label="Lab Submission Date"
              name="lab_date"
              onChange={handleInputChange}
              // prefer the explicit lab_submission_date (a Date or ISO string),
              // otherwise fall back to any created_at value
              value={values.lab_date}
              labelOn={true}
              type="text"
              dateFormat={"LLL dd, yyyy"}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              // error={errors.created_at}
            />

            <DatePickerInput
              label="Lab Completed Date"
              name="completed_date"
              onChange={handleInputChange}
              value={values.completed_date}
              labelOn={true}
              type="text"
              dateFormat={"LLL dd, yyyy"}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mt-6">
            <MediaUi
              title="Document 1"
              uploadedImage={media1}
              setUploadedImage={setMedia1}
              isEdit={true}
              imageUrl={null}
              imageName="uploaded_media_1"
              handleInputChange={() => {}}
              onDelete={() => setMedia1(null)}
            />
            <MediaUi
              title="Document 2"
              uploadedImage={media2}
              setUploadedImage={setMedia2}
              isEdit={true}
              imageUrl={null}
              imageName="uploaded_media_2"
              handleInputChange={() => {}}
              onDelete={() => setMedia2(null)}
            />
            <MediaUi
              title="Document 3"
              uploadedImage={media3}
              setUploadedImage={setMedia3}
              isEdit={true}
              imageUrl={null}
              imageName="uploaded_media_3"
              handleInputChange={() => {}}
              onDelete={() => setMedia3(null)}
            />
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 mr-3"
              onClick={() => navigate("/lab-samples")}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ml-4 inline-flex justify-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white"
              onClick={handleSubmit}
            >
              <ButtonSpinner spin={inProgress} text="Create" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(actions.logout()),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(CreateLabSample)
);
